import { Controller } from "@hotwired/stimulus"
import {
  DEFAULT_SHRINK_LEVEL,
  formatBytes,
  isPdfFile,
  reductionSummary,
  shrunkFilename
} from "../lib/pdf_shrink_helpers.mjs"

// Connects to data-controller="pdf-shrink"
//
// Drives the "Shrink PDF" modal on the admin charity care application page.
// The heavy lifting (pdf.js + pdf-lib) lives in the on-demand bundle
// app/javascript/pdf_shrink.js, injected the first time a file is shrunk so the
// libraries stay out of application.js.
//
// Values:
//   - scriptUrl:     digested asset URL of the pdf_shrink.js bundle
//   - uploadInputId: id of the page's existing "Upload PDF" file input; the
//                    "Upload to this application" button hands the shrunk file
//                    to that input so it goes through the normal upload path.

let libraryPromise = null

export default class extends Controller {
  static targets = [
    "input", "dropzone", "emptyState", "fileState", "fileName", "fileSize",
    "options", "submit", "submitRow", "progress", "progressBar", "progressText",
    "result", "originalSize", "newSize", "reduction", "resultNote",
    "download", "upload", "clear", "error"
  ]

  static values = {
    scriptUrl: { type: String, default: "" },
    uploadInputId: { type: String, default: "" }
  }

  connect() {
    this.file = null
    this.resultBlob = null
    this.busy = false
    this.uploading = false
    this.onUploadError = this.onUploadError.bind(this)
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    this.render()
  }

  disconnect() {
    this.stopWatchingUpload()
    this.revokeDownloadUrl()
  }

  // --- file selection -----------------------------------------------------

  pick(event) {
    const file = event.target.files && event.target.files[0]
    this.selectFile(file)
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.dataset.dragging = "true"
  }

  dragLeave(event) {
    event.preventDefault()
    // Moving over a child of the dropzone fires dragleave too; ignore that.
    if (event.relatedTarget && this.dropzoneTarget.contains(event.relatedTarget)) return
    delete this.dropzoneTarget.dataset.dragging
  }

  drop(event) {
    event.preventDefault()
    delete this.dropzoneTarget.dataset.dragging
    if (this.busy) return
    const file = event.dataTransfer && event.dataTransfer.files[0]
    this.selectFile(file)
  }

  // Changing the level after a shrink invalidates the old result so Download /
  // Upload can never hand over a file made at a different level.
  levelChanged() {
    if (this.busy || !this.resultBlob) return
    this.resetResult()
    this.render()
  }

  clear(event) {
    if (event) event.preventDefault()
    if (this.busy) return
    this.file = null
    this.inputTarget.value = ""
    this.resetResult()
    this.render()
  }

  selectFile(file) {
    if (!file) return
    this.resetResult()
    if (!isPdfFile(file)) {
      this.file = null
      this.showError("That file isn't a PDF. Choose a .pdf file.")
      this.render()
      return
    }
    this.file = file
    this.render()
  }

  // --- shrinking -----------------------------------------------------------

  async shrink(event) {
    if (event) event.preventDefault()
    if (this.busy || !this.file) return

    this.busy = true
    this.resetResult()
    this.setProgress(0, 0)
    this.render()

    try {
      const lib = await this.loadLibrary()
      const blob = await lib.shrink(this.file, {
        level: this.selectedLevel,
        onProgress: ({ done, pages }) => this.setProgress(done, pages)
      })
      this.resultBlob = blob
      this.showResult(blob)
    } catch (error) {
      console.error("[pdf-shrink] Failed to shrink PDF:", error)
      const message = error && error.name === "PasswordException"
        ? "This PDF is password-protected, so it can't be shrunk here."
        : "Couldn't shrink this PDF. Try a different compression level or file."
      this.showError(message)
    } finally {
      this.busy = false
      this.render()
    }
  }

  get selectedLevel() {
    const checked = this.optionsTarget.querySelector("input[type=radio]:checked")
    return checked ? checked.value : DEFAULT_SHRINK_LEVEL
  }

  // `done` pages of `pages` have finished rendering.
  setProgress(done, pages) {
    const percent = pages > 0 ? Math.round((done / pages) * 100) : 0
    this.progressBarTarget.style.width = `${Math.max(percent, 4)}%`
    this.progressBarTarget.parentElement.setAttribute("aria-valuenow", String(percent))
    if (pages === 0) {
      this.progressTextTarget.textContent = "Loading PDF tools…"
    } else if (done >= pages) {
      this.progressTextTarget.textContent = "Saving the smaller PDF…"
    } else {
      this.progressTextTarget.textContent = `Shrinking page ${done + 1} of ${pages}…`
    }
  }

  showResult(blob) {
    const summary = reductionSummary(this.file.size, blob.size)
    this.originalSizeTarget.textContent = formatBytes(this.file.size)
    this.newSizeTarget.textContent = formatBytes(blob.size)
    this.reductionTarget.textContent = summary.label
    this.reductionTarget.dataset.smaller = summary.smaller ? "true" : "false"
    this.resultNoteTarget.hidden = summary.smaller

    this.revokeDownloadUrl()
    this.downloadUrl = URL.createObjectURL(blob)
    this.downloadTarget.href = this.downloadUrl
    this.downloadTarget.download = shrunkFilename(this.file.name)
  }

  // --- hand-off to the page's existing Upload PDF form ---------------------

  upload(event) {
    if (event) event.preventDefault()
    if (this.busy || !this.resultBlob) return
    const input = this.uploadInputIdValue && document.getElementById(this.uploadInputIdValue)
    if (!input || typeof DataTransfer === "undefined") {
      this.showError("Couldn't attach the file automatically. Download it and use Upload PDF instead.")
      return
    }

    const file = new File([this.resultBlob], shrunkFilename(this.file.name), { type: "application/pdf" })
    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.files = transfer.files

    this.resetError()
    this.setUploading(true)
    this.watchUpload(input)
    // The existing Upload PDF input submits its form on change. On success the
    // server's upload_success stream reloads the page; failures come back via
    // direct-upload:error or a failed turbo:submit-end and re-enable the tool.
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  watchUpload(input) {
    this.stopWatchingUpload()
    this.uploadInput = input
    this.uploadForm = input.form
    input.addEventListener("direct-upload:error", this.onUploadError)
    if (this.uploadForm) this.uploadForm.addEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  stopWatchingUpload() {
    if (this.uploadInput) this.uploadInput.removeEventListener("direct-upload:error", this.onUploadError)
    if (this.uploadForm) this.uploadForm.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.uploadInput = null
    this.uploadForm = null
  }

  onUploadError(event) {
    // Let Active Storage's default alert() be replaced by our inline message.
    event.preventDefault()
    this.uploadFailed()
  }

  onSubmitEnd(event) {
    if (event.detail && event.detail.success) {
      this.stopWatchingUpload()
      return
    }
    this.uploadFailed()
  }

  uploadFailed() {
    this.stopWatchingUpload()
    this.setUploading(false)
    this.showError("Upload failed. Try again, or download the file and use Upload PDF.")
  }

  setUploading(uploading) {
    this.uploading = uploading
    this.busy = uploading
    this.uploadTarget.setAttribute("aria-busy", uploading ? "true" : "false")
    this.uploadTarget.querySelector("[data-label]").textContent = uploading ? "Uploading…" : "Upload to application"
    this.render()
  }

  // --- helpers --------------------------------------------------------------

  loadLibrary() {
    if (window.GBPdfShrink) return Promise.resolve(window.GBPdfShrink)
    if (!this.scriptUrlValue) return Promise.reject(new Error("Missing pdf_shrink bundle URL"))
    if (!libraryPromise) {
      libraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = this.scriptUrlValue
        script.async = true
        script.addEventListener("load", () => {
          if (window.GBPdfShrink) {
            resolve(window.GBPdfShrink)
          } else {
            libraryPromise = null
            reject(new Error("pdf_shrink bundle loaded but global missing"))
          }
        })
        script.addEventListener("error", () => {
          libraryPromise = null
          reject(new Error("Failed to load pdf_shrink bundle"))
        })
        document.head.appendChild(script)
      })
    }
    return libraryPromise
  }

  resetResult() {
    this.resultBlob = null
    this.revokeDownloadUrl()
    this.resetError()
  }

  resetError() {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  showError(message) {
    // Unhide first so the role=alert region announces the new text.
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  revokeDownloadUrl() {
    if (this.downloadUrl) URL.revokeObjectURL(this.downloadUrl)
    this.downloadUrl = null
  }

  render() {
    const hasFile = Boolean(this.file)
    const hasResult = Boolean(this.resultBlob)

    this.emptyStateTarget.hidden = hasFile
    this.fileStateTarget.hidden = !hasFile
    if (hasFile) {
      this.fileNameTarget.textContent = this.file.name
      this.fileSizeTarget.textContent = formatBytes(this.file.size)
    }

    this.dropzoneTarget.dataset.disabled = this.busy ? "true" : "false"
    this.inputTarget.disabled = this.busy
    this.optionsTarget.querySelectorAll("input").forEach((input) => { input.disabled = this.busy })

    this.submitTarget.disabled = !hasFile || this.busy
    this.submitRowTarget.hidden = hasResult || this.busy
    this.progressTarget.hidden = !this.busy || hasResult
    this.resultTarget.hidden = !hasResult
    if (hasResult) {
      this.uploadTarget.disabled = this.busy
      this.clearTarget.disabled = this.busy
    }
  }
}
